import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { questionStats, reviewQueue } from "../../utils/jobs";
import { openApplication, openMessage } from "../../utils/extension";
import PlatypusLogo from "../../components/PlatypusLogo";

function ReviewStep({ icon, label, detail, warn, onClick }) {
  return (
    <button type="button" className="review-step" onClick={onClick}>
      <i className={`bi ${warn ? "bi-exclamation-circle-fill text-warning" : icon}`} />
      <span className="min-w-0">
        <span className="d-block fw-medium">{label}</span>
        <span className="d-block text-muted text-truncate" style={{ fontSize: 12 }}>
          {detail}
        </span>
      </span>
    </button>
  );
}

const nextInQueue = (jobs, job) => reviewQueue(jobs).find((j) => j.id !== job.id);

// Shown for a while after a prepared job is applied to (by you or by the extension spotting the confirmation page).
export function AppliedBanner({ job }) {
  const { jobs, undoApplied, toast } = useApp();
  const navigate = useNavigate();
  const next = nextInQueue(jobs, job);
  const left = reviewQueue(jobs).length;

  const undo = async () => {
    if (await undoApplied(job.id)) toast("Marked as not applied yet", "info");
  };

  return (
    <div className="card platypus-banner mb-3">
      <div className="card-body d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
        <div className="flex-grow-1">
          <div className="fw-semibold">
            <i className="bi bi-check2-circle text-success me-1" /> Applied to {job.company}
          </div>
          <div className="small text-muted">
            {left ? `${left} more prepared job${left === 1 ? "" : "s"} in your queue.` : "That was the last job in your queue."}
          </div>
        </div>
        <button className="btn btn-link text-muted" onClick={undo}>
          Undo
        </button>
        {next ? (
          <button className="btn btn-primary" onClick={() => navigate(`/jobs/${next.id}`)}>
            Next job <i className="bi bi-arrow-right ms-1" />
          </button>
        ) : (
          <button className="btn btn-light" onClick={() => navigate("/platypus")}>
            Back to Platypus
          </button>
        )}
      </div>
    </div>
  );
}

// Shown on jobs Platypus prepared, until they're applied to or skipped.
function PlatypusBanner({ job, onGo }) {
  const { jobs, deleteJob, completeStep, toast } = useApp();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const [opened, setOpened] = useState(false);
  const { matchScore, matchReason, concerns = [] } = job.automator;
  const q = questionStats(job);
  const edits = (job.tailoredResume?.suggestions || []).filter((s) => s.accepted).length;
  const next = nextInQueue(jobs, job);
  const goNext = () => navigate(next ? `/jobs/${next.id}` : "/platypus");

  const open = async () => {
    setOpening(true);
    const result = await openApplication(job);
    setOpening(false);
    setOpened(true);
    const [message, variant] = openMessage(result);
    toast(message, variant);
  };

  const submitted = async () => {
    if (!(await completeStep(job.id, "applied", "Submitted application"))) return;
    toast(next ? `Marked ${job.company} as applied. Here's the next one.` : `Marked ${job.company} as applied. Your queue is empty.`);
    goNext();
  };

  const skip = async () => {
    const { title, company } = job;
    goNext();
    if (await deleteJob(job.id)) toast(`Skipped ${title} at ${company}. It won't come back.`, "info");
  };

  const answers = q.total
    ? `${q.answered} answered${q.needsYou ? `, ${q.needsYou} need${q.needsYou === 1 ? "s" : ""} you` : ""}`
    : job.automator.formRead
      ? "No extra questions"
      : "Answered on the page";

  return (
    <div className="card platypus-banner mb-3">
      <div className="card-body">
        <div className="d-flex gap-3 align-items-start mb-3">
          <div className="match-badge" style={{ "--c": matchScore >= 80 ? "#16a34a" : "#36454f", width: 52, height: 52, fontSize: "1.15rem" }}>
            {matchScore}
            <small>%</small>
          </div>
          <div className="min-w-0">
            <div className="fw-semibold"><PlatypusLogo size={24} className="me-1 align-middle" /> Prepared by Platypus. Review it, then open the application.</div>
            {matchReason && <div className="small text-muted">{matchReason}</div>}
            {concerns.length > 0 && (
              <div className="d-flex flex-wrap gap-1 mt-1">
                {concerns.map((c) => (
                  <span key={c} className="tag tag-warning">
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="review-steps mb-3">
          <ReviewStep
            icon="bi-file-earmark-person text-primary"
            label="Resume"
            detail={job.tailoredResume ? `${edits} edit${edits === 1 ? "" : "s"}, ${job.tailoredResume.scoreBefore}% → ${job.tailoredResume.scoreAfter}% match` : "Not tailored"}
            onClick={() => onGo("resume")}
          />
          <ReviewStep
            icon="bi-envelope-paper text-primary"
            label="Cover letter"
            detail={job.noCoverLetter ? "Not required" : job.coverLetter ? "Written" : "Not written"}
            onClick={() => onGo("cover-letter")}
          />
          <ReviewStep
            icon="bi-ui-checks text-primary"
            label="Questions"
            detail={answers}
            warn={q.needsYou > 0}
            onClick={() => onGo("apply")}
          />
        </div>

        <div className="d-flex flex-column flex-sm-row gap-2">
          <button className={`btn ${opened ? "btn-light" : "btn-primary"}`} onClick={open} disabled={opening || !(job.applyUrl || job.url)}>
            {opening ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-box-arrow-up-right me-1" />}
            Open application
          </button>
          {opened && (
            <button className="btn btn-success" onClick={submitted}>
              <i className="bi bi-check2-circle me-1" /> I submitted it
            </button>
          )}
          <button className="btn btn-link text-muted ms-sm-auto" onClick={skip}>
            Skip this job
          </button>
        </div>
        {opened && (
          <p className="small text-muted mt-2 mb-0">
            Check the filled-in form in the new tab, fix anything outlined in orange, and submit it. Plateo marks it applied when it sees the
            confirmation page; if it doesn't, click I submitted it.
          </p>
        )}
      </div>
    </div>
  );
}

export default PlatypusBanner;
